import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import * as con from "../../GlobalConstants";
import { getRequestConfig } from "../logic/supportFunctions";
import { fetchUserLines, resetUserLines } from "./userLinesSlice";
import { addMonths, getPreviousWorkingDay, getToday } from "../../utils/dateFunctions";
import { exportModelParameters } from "./modelParametersSlice";
import { exportUserLineApplicationParameters, resetUserLineApplicationParameters } from "./userLineApplicationParametersSlice";
import { setSurrogateUser } from "./authSlice";
import * as sf from "./../logic/supportFunctions"
import { arrayToObjectByAttribute } from "../../GlobalFunctions";
import initialParameters from "../../config/initial_parameters.json"
import { makeAllManagementReport, makeCommitteeReport, makePositionReport, makePurchaseReport, makeMarketDataReport  } from "../../utils/reportFunctions";
import { createError, createNotification } from "../../templates/notifications/Notifications";
import { extractErrorMessage } from "../../utils/errorFunctions";

// =====================================================
// CHECK SERVER
// =====================================================
export const checkServer = createAsyncThunk(
  "applicationParameters/checkServer",
  async (_, { rejectWithValue }) => {
    try {
      await axios.get(con.rest_base_url + con.server_check);
      return { status: con.OK };
    } catch (err) {
      return rejectWithValue({ status: con.ERROR });
    }
  }
);

// =====================================================
// FETCH SURROGATE USER LIST
// =====================================================
export const fetchSurrogateUserList = createAsyncThunk(
  "applicationParameters/fetchSurrogateUserList",
  async (_, { rejectWithValue }) => {

    const config = getRequestConfig();

    try {
      const res = await axios.get(
        con.rest_base_url + con.surrogate_user_list_api,
        config
      );

      // Transform array -> { id: object }
      const surrogateUsers = res.data.reduce((acc, ob) => {
        acc[ob[con.ID]] = ob;
        return acc;
      }, {});

      return surrogateUsers;
    } catch (err) {
      return rejectWithValue(extractErrorMessage(err));
    }
  }
);

// =====================================================
// SAVE USER INFORMATION
// =====================================================
export const saveUserInformation = createAsyncThunk(
  "applicationParameters/saveUserInformation",
  async (_, { dispatch, rejectWithValue }) => {
    try {

      // 1️⃣ Export model parameters
      await dispatch(exportModelParameters()).unwrap();

      // 2️⃣ Export user line parameters
      await dispatch(exportUserLineApplicationParameters()).unwrap();

      return true;

    } catch (err) {
      return rejectWithValue(extractErrorMessage(err, "Hubo un error guardando la información"));
    }
  }
);

// =====================================================
// SET SURROGATE USER
// =====================================================
export const setSurrogateUserById = createAsyncThunk(
  "application/setSurrogateUserById",
  async (surrogateUserId, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState();

      const surrogateDict =
        state[con.REDUCER_APPLICATION_PARAMETERS][con.SURROGATE_USER_DICT];

      let user = sf.getUser();

      if (surrogateUserId in surrogateDict) {
        user = surrogateDict[surrogateUserId];
      }

      // 1️⃣ Set surrogate in auth slice
      dispatch(setSurrogateUser(user));

      // 2️⃣ Reset selected line
      dispatch(setApplicationParameter({
        id: con.SELECTED_LINE,
        value: null
      }));

      // 3️⃣ Reset user line application parameters
      dispatch(resetUserLineApplicationParameters());

      // 4️⃣ Clear user lines
      dispatch(resetUserLines());

      return user;

    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

// =====================================================
// FETCH MARKET ASSETS
// =====================================================
export const fetchMarketAssets = createAsyncThunk(
  "applicationParameters/fetchMarketAssets",
  async (_, { rejectWithValue }) => {

    const config = getRequestConfig();

    try {
      const res = await axios.get(
        con.rest_base_url + con.market_assets_url,
        config
      );

      // Transform array -> { id: object }
      const marketAssets = arrayToObjectByAttribute(res.data, con.ID)

      return marketAssets;
    } catch (err) {
      return rejectWithValue(extractErrorMessage(err));
    }
  }
);

// =====================================================
// CHECK PASSWORD
// =====================================================
export const checkPassword = createAsyncThunk(
  "applicationParameters/checkPassword",
  async (password, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const username = state[con.REDUCER_AUTHENTICATION][con.USER][con.USERNAME]
      const config = getRequestConfig()

      await axios.post(con.rest_base_url + con.check_password_api, { username, password }, config)

      return con.OK
    } catch (error) {
      if (error.response?.data?.non_field_errors?.[0] === con.WRONG_CREDENTIALS)
        return rejectWithValue(con.WRONG_CREDENTIALS)

      return rejectWithValue(con.ERROR)
    }
  }
)

const initialState = {

  [con.SERVER]: { [con.STATUS]: con.NOT_STARTED },
  [con.CURRENT_DATE] : getToday().toISO(),
  [con.PENDING_ROUTE]: null,
  [con.PENDING_SURROGATE_USER_ID]: null,
  [con.PENDING_USER_LINE_ID]: null,
  [con.SHOW_UNSAVED_MODAL]: false,

  [con.LOGGED_IN]: { [con.STATUS]: con.NOT_STARTED },

  [con.SELECTED_LINE]: null,
  [con.SELECTED_SCENARIO]: con.SCENARIO_VAR_UP,
  
  [con.VISUALIZATION_PERIODICITY] : con.MONTH,

  [con.CHECK_PASSWORD]: con.NOT_STARTED,
  [con.CHANGE_PASSWORD]: con.NOT_STARTED,

  [con.HAS_TRANSACTIONS]: false,

  [con.SURROGATE_USER_DICT]: {},
  surrogate_user_dict_status: con.NOT_STARTED,
  
  [con.USER_INFORMATION_SAVED]: con.NOT_STARTED,

  [con.MARKET_ASSETS]: {[con.COP_ASSET[con.ID]] : {...con.COP_ASSET}},
  market_assets_status: con.NOT_STARTED,
  [con.SELECTED_MARKET_ASSET] : con.COP_ASSET[con.ID],

  [con.CARRY_OVER_COVERAGE] : false, // Variable indicating if the over coverages should be carried to the next period
  [con.SELECTED_VAR] : con.VAR, // Selected VaR origin (Opportunistic, High, middle or lower band)
  [con.SELECTED_BAND] : con.LOWER_BAND, // Selected band for visualization

  [con.ADVISOR_SAVED] : con.NOT_STARTED, // Status object for saving an advisor
  [con.MAIN_ADVISOR_SAVED] : con.NOT_STARTED, // Status object for saving the main advisor  

  // Forward Assessment
  [con.FORWARD_ASSESSMENT_REFERENCE_DATE] : getPreviousWorkingDay().toISO(), // Forward Assessment reference Date 
  [con.FORWARD_ASSESSMENT_START_DATE] : addMonths(getPreviousWorkingDay(), -3, true).toISO(), // Forward Assessment Start Date
  [con.FORWARD_ASSESSMENT_RATE] : initialParameters[con.DEFAULT_SPOT], // Forward Assessment Used Rate
  [con.FORWARD_ASSESSMENT_RATE_ORIGIN] : con.SPOT, // Forward Assessment Rate type (TRM, Spot, Close, etc..)
  [con.FORWARD_ASSESSMENT_INCLUDE_PURCHASES] : true, // Forward Assessment Include purchases by default
  [con.FORWARD_ASSESSMENT_INCLUDE_SALES] : true, // Forward Assessment Include sales by default
  [con.FORWARD_ASSESSMENT_SHOW_HIDDEN_COLUMNS] : false, // Show the hidden values in the Forward Assessment page 
  [con.FORWARD_ASSESSMENT_SHOW_ACTIVE_COVERAGES] : true, // Show the active forward coverages in the Forward Assessment page 
  [con.FORWARD_ASSESSMENT_SHOW_EXPIRED_COVERAGES] : false, // Show the expired forward coverages in the Forward Assessment page 
  [con.FORWARD_ASSESSMENT_REPORT] : {[con.REPORT_FILE_NAME] : null, // Parameters for report the Forward Assessment report generation 
                                      [con.GENERATING] : false},  

  // Option Assessment
  [con.OPTION_ASSESSMENT_REFERENCE_DATE] : getPreviousWorkingDay().toISO(),
  [con.OPTION_ASSESSMENT_START_DATE] : addMonths(getPreviousWorkingDay(), -3, true).toISO(), // Option Assessment Start Date
  [con.OPTION_ASSESSMENT_RATE] : initialParameters[con.DEFAULT_SPOT], // Option Assessment Used Rate
  [con.OPTION_ASSESSMENT_RATE_ORIGIN] : con.SPOT, // Option Assessment Rate type (TRM, Spot, Close, etc..) 
  [con.OPTION_ASSESSMENT_INCLUDE_PURCHASES] : true, // Option Assessment Include purchases by default
  [con.OPTION_ASSESSMENT_INCLUDE_SALES] : true, // Option Assessment Include sales by default
  [con.OPTION_ASSESSMENT_INCLUDE_CALL] : true, // Option Assessment Include calls by default
  [con.OPTION_ASSESSMENT_INCLUDE_PUT] : true, // Option Assessment Include puts by default
  [con.OPTION_ASSESSMENT_SHOW_HIDDEN_COLUMNS] : false, // Show the hidden values in the Option Assessment page 
  [con.OPTION_ASSESSMENT_SHOW_ACTIVE_COVERAGES]: true, // Show the active options coverages in the Forward Assessment page 
  [con.OPTION_ASSESSMENT_SHOW_EXPIRED_COVERAGES] : false, // Show the expired Option coverages in the Forward Assessment page 
  [con.OPTION_ASSESSMENT_REPORT] : {[con.REPORT_FILE_NAME] : null, // Parameters for report the Option Assessment report generation 
                                      [con.GENERATING] : false},
                                      
  //Position report
  [con.POSITION_REPORT] : con.NOT_STARTED,
   //Purchase report
  [con.PURCHASE_REPORT] : con.NOT_STARTED,
  //Committee report
  [con.COMMITTEE_REPORT] : con.NOT_STARTED,
  //Market data
  [con.MARKET_DATA_REPORT]: con.NOT_STARTED,
};

const applicationParametersSlice = createSlice({
  name: con.REDUCER_APPLICATION_PARAMETERS,
  initialState,
  reducers: {
    setApplicationParameter: (state, action) => {
      const { id, value } = action.payload;
      state[id] = value;
    },

    replaceApplicationParameters: (state, action) => {
      Object.assign(state, action.payload);
    },

  },
  extraReducers: (builder) => {
    builder

      // ================= CHECK SERVER =================
      .addCase(checkServer.pending, (state) => {
        state[con.SERVER][con.STATUS] = con.LOADING;
      })
      .addCase(checkServer.fulfilled, (state, action) => {
        state[con.SERVER][con.STATUS] = action.payload.status;
      })
      .addCase(checkServer.rejected, (state, action) => {
        state[con.SERVER][con.STATUS] = action.payload.status;
      })

      // ================= SURROGATE USER DICT =================
      .addCase(fetchSurrogateUserList.pending, (state) => {
        state.surrogate_user_dict_status = con.LOADING;
      })
      .addCase(fetchSurrogateUserList.fulfilled, (state, action) => {
        state[con.SURROGATE_USER_DICT] = action.payload
        state.surrogate_user_dict_status = con.OK
      })
      .addCase(fetchSurrogateUserList.rejected, (state, action) => {
        state.surrogate_user_dict_status = con.ERROR;
        createError(action.payload);
      })

      // ================= SELECT LINE WHEN FETCH USERLINES =================
      .addCase(fetchUserLines.fulfilled, (state, action) => {
        const lines = action.payload

        if (lines.length > 0 && !state[con.SELECTED_LINE]) {
          state[con.SELECTED_LINE] = lines[0][con.ID]
        }
      })

      // ================= SAVE USER INFO =================
      .addCase(saveUserInformation.pending, (state) => {
        state[con.USER_INFORMATION_SAVED] = con.LOADING;
      })
      .addCase(saveUserInformation.fulfilled, (state) => {
        state[con.USER_INFORMATION_SAVED] = con.OK;
        createNotification("Información guardada correctamente", con.NOTIFICATION_TIME_SHORT);
      })
      .addCase(saveUserInformation.rejected, (state, action) => {
        state[con.USER_INFORMATION_SAVED] = con.ERROR;
        createError(action.payload);
      })

      // ================= MARKET ASSETS =================
      .addCase(fetchMarketAssets.pending, (state) => {
        state.market_assets_status = con.LOADING;
      })
      .addCase(fetchMarketAssets.fulfilled, (state, action) => {
        state.market_assets_status = con.OK
        state[con.MARKET_ASSETS] = action.payload;
      })
      .addCase(fetchMarketAssets.rejected, (state, action) => {
        state.market_assets_status = con.ERROR;
        createError(action.payload);
      })     
      
      // ================= CHECK PASSWORD =================
      .addCase(checkPassword.pending, (state) => {
        state[con.CHECK_PASSWORD] = con.LOADING;
      })
      .addCase(checkPassword.fulfilled, (state) => {
        state[con.CHECK_PASSWORD] = con.OK;
      })
      .addCase(checkPassword.rejected, (state, action) => {
        state[con.CHECK_PASSWORD] = action.payload // con.WRONG_CREDENTIALS o con.ERROR
      })

      // ================== POSITION REPORT ===============
      .addCase(makePositionReport.pending, (state) => {
        state[con.POSITION_REPORT] = con.GENERATING
      })
      .addCase(makePositionReport.fulfilled, (state) => {
        state[con.POSITION_REPORT] = con.OK;
        createNotification("Reporte de posición generado", con.NOTIFICATION_TIME_SHORT);
      })
      .addCase(makePositionReport.rejected, (state, action) => {
        state[con.POSITION_REPORT] = con.ERROR;
        createError(action.payload);
      })

      //===================PURCHASE REPORT ================
      .addCase(makePurchaseReport.pending, (state) => {
        state[con.PURCHASE_REPORT] = con.GENERATING
      })
      .addCase(makePurchaseReport.fulfilled, (state) => {
        state[con.PURCHASE_REPORT] = con.OK;
        createNotification("Reporte de compras generado", con.NOTIFICATION_TIME_SHORT);
      })
      .addCase(makePurchaseReport.rejected, (state, action) => {
        state[con.PURCHASE_REPORT] = con.ERROR;
        createError(action.payload);
      })

      //====================COMMITTEE REPORT ==============
      .addCase(makeCommitteeReport.pending, (state) => {
        state[con.COMMITTEE_REPORT] = con.GENERATING
      })
      .addCase(makeCommitteeReport.fulfilled, (state) => {
        state[con.COMMITTEE_REPORT] = con.OK;
        createNotification("Reporte de comité generado", con.NOTIFICATION_TIME_SHORT);
      })
      .addCase(makeCommitteeReport.rejected, (state, action) => {
        state[con.COMMITTEE_REPORT] = con.ERROR;
        createError(action.payload);
      })

    // =================ALL MANAGEMENT REPORT ============
    .addCase(makeAllManagementReport.pending, (state) => {
      state[con.ALL_MANAGEMENT_REPORT] = con.GENERATING
    })
    .addCase(makeAllManagementReport.fulfilled, (state) => {
      state[con.ALL_MANAGEMENT_REPORT] = con.OK;
      createNotification("Reporte de gestión generado", con.NOTIFICATION_TIME_SHORT);
    })
    .addCase(makeAllManagementReport.rejected, (state, action) => {
      state[con.ALL_MANAGEMENT_REPORT] = con.ERROR
      createError(action.payload);
    })
    
    // ================= MARKET DATA REPORT =================
    .addCase(makeMarketDataReport.pending, (state) => {
      state[con.MARKET_DATA_REPORT] = con.GENERATING;
    })
    .addCase(makeMarketDataReport.fulfilled, (state) => {
      state[con.MARKET_DATA_REPORT] = con.OK;
      createNotification("Reporte de datos de mercado generado", con.NOTIFICATION_TIME_SHORT);
    })
    .addCase(makeMarketDataReport.rejected, (state, action) => {
      state[con.MARKET_DATA_REPORT] = con.ERROR;
      createError(action.payload);
    })
      
}});

export const {
  setApplicationParameter,
  replaceApplicationParameters,
} = applicationParametersSlice.actions;

export default applicationParametersSlice.reducer;
